import { Bell, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useMyDevices, useNotifyThisDevice, useRemoveDevice, useThisDeviceEndpoint } from '@/data/push';
import { appEnv } from '@/lib/env';
import { browserPushSupport, type PushSupport } from '@/lib/push';

const WHY_NOT: Record<Exclude<PushSupport, 'ok'>, string> = {
  unsupported:
    'This browser can’t get notifications when Hive is closed. The desktop app notifies while it’s open; for your phone, use the installed app.',
  'install-first':
    'On iPhone and iPad, add Hive to your Home Screen (Share → Add to Home Screen), then turn this on from there.',
  'not-configured': 'Phone notifications aren’t set up for this build yet (VITE_VAPID_PUBLIC_KEY).',
};

/**
 * Account settings' notifications part: turn on notifications for this device
 * (so mentions and messages reach it with Hive closed), and see and remove the
 * devices that get them. Same rules as desktop notifications.
 */
export function PushDevices({ support = undefined }: { support?: PushSupport }) {
  const vapidKey = appEnv.ok ? appEnv.env.VITE_VAPID_PUBLIC_KEY : undefined;
  const status = support ?? browserPushSupport(vapidKey);
  const devices = useMyDevices();
  const thisEndpoint = useThisDeviceEndpoint();
  const notify = useNotifyThisDevice();
  const remove = useRemoveDevice();
  const thisOn = !!thisEndpoint.data && (devices.data ?? []).some((d) => d.endpoint === thisEndpoint.data);

  return (
    <section aria-label="Notifications on your devices" className="flex flex-col gap-2 border-t border-line pt-3">
      <h3 className="hud-label">Notifications when Hive is closed</h3>
      {status === 'ok' ? (
        thisOn ? (
          <p className="text-xs text-muted">This device gets notifications.</p>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              disabled={notify.isPending || !vapidKey}
              onClick={() => vapidKey && notify.mutate(vapidKey)}
            >
              <Bell aria-hidden /> Notify this device
            </Button>
            <span className="text-xs text-dim">Mentions and messages, by your channel settings.</span>
          </div>
        )
      ) : (
        <p className="text-xs text-dim">{WHY_NOT[status]}</p>
      )}
      {notify.error ? (
        <p role="alert" className="font-mono text-xs text-red">
          {notify.error.message}
        </p>
      ) : null}
      {devices.data?.length ? (
        <ul className="flex flex-col gap-px" aria-label="Devices">
          {devices.data.map((d) => (
            <li key={d.id} className="flex items-center gap-2 bg-bg-3/60 px-2.5 py-1.5 text-sm">
              <span className="flex-1 truncate">
                {d.device_name || 'A device'}
                {d.endpoint === thisEndpoint.data ? (
                  <span className="ml-1.5 font-mono text-[10px] text-cyan">this one</span>
                ) : null}
              </span>
              <button
                type="button"
                aria-label={`Stop notifications to ${d.device_name || 'this device'}`}
                disabled={remove.isPending}
                onClick={() => remove.mutate(d)}
                className="cursor-pointer p-1 text-dim hover:text-red"
              >
                <Trash2 className="size-3.5" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
